// Chat Widget - Embeddable AI Chat Assistant with CSS Isolation
// Entry point that imports all modular components

import { DEFAULT_CONFIG } from './config.js';
import { detectLanguage, i18n, t, setLanguage, translateValidationError } from './modules/i18n.js';
import {
    initializeState,
    saveMessages,
    saveUserDetails,
    loadMessages,
    saveSessionId,
    savePropertyId,
    saveUserDetailsCaptured,
    loadReopenAfterRedirect,
    loadManuallyClosed,
    saveManuallyClosed,
    setStorageKeyPrefix,
    loadWidgetHeight,
    saveWidgetHeight
} from './modules/state.js';
import { VALID_MARKERS, stripUnknownMarkers, stripCaptureMarkers, hasMarker } from './modules/markers.js';
import { renderMarkdown } from './modules/markdown.js';
import { formatPhoneNumber, formatPhoneNumberRealTime, validatePhoneNumber, validateEmail } from './utils/helpers.js';
import {
    createWidget,
    updateStyles,
    updateBotIconElements,
    updateNudgeText,
    renderMessages,
    showTypingIndicator,
    hideTypingIndicator,
    scrollToBottom,
    scrollToShowRecentMessages,
    showError,
    open,
    close,
    showMobileNotification,
    hideMobileNotification,
    showNudge,
    hideNudge,
    getWelcomeHTML,
    showBackButton,
    hideBackButton
} from './modules/ui.js';
import {
    getUserDetailsFormHTML,
    getUserDetailsConfirmationHTML,
    getUserDetailsSubmittedHTML,
    getEmailFormHTML,
    getEmailConfirmationHTML,
    getPhoneFormHTML,
    getPhoneConfirmationHTML,
    getAppointmentFormHTML,
    setupUserDetailsFormListeners,
    setupPhoneFormListeners,
    setupAppointmentFormListeners,
    validateUserDetailsForm,
    validateAppointmentForm
} from './modules/forms.js';
import {
    handleAPIError,
    initializeSession as initSession,
    sendQuery,
    submitUserDetailsAPI,
    submitPhoneAPI,
    submitEmailAPI,
    submitAppointmentAPI,
    sendConversationEmailAPI,
    submitContactUsAPI,
    saveChatLogAPI,
    setAPIPropertyID,
    setPreviewMode
} from './modules/api.js';
import {
    addMessage as addMsg,
    showSuggestedQuestions,
    hideSuggestedQuestions,
    showSuggestedQuestionsIfHidden,
    addChoiceButtonsToMessage,
    showRedirectConfirmation
} from './modules/chat.js';

class ChatWidget {
    constructor(config = {}) {
        // Store original user-passed config (before merging with defaults)
        // This is used to preserve explicit user overrides when fetching server config
        this._userConfig = { ...config };

        // Merge config with defaults
        this.config = { ...DEFAULT_CONFIG, ...config };

        // Set API endpoint based on preview mode
        // Preview mode uses /api/query-preview (includes draft flows for admin testing)
        if (this.config.previewMode) {
            this.apiEndpoint = '/api/query-preview';
            setPreviewMode(true);
        } else {
            this.apiEndpoint = this.config.apiEndpoint;
        }

        // Extract base URL from apiEndpoint for other API calls
        if (this.apiEndpoint.startsWith('http')) {
            const url = new URL(this.apiEndpoint);
            this.baseUrl = url.origin;
        } else {
            this.baseUrl = '';
        }

        // Initialize language
        this.language = this.config.language || detectLanguage(i18n);

        // Initialize state
        const state = initializeState(this.config);
        Object.assign(this, state);

        // Create widget DOM
        const elements = createWidget(this.config);
        Object.assign(this, elements);

        // Property ID for multi-tenancy
        // Use stored property ID if available (from previous session init)
        this.propertyID = state.storedPropertyId || null;
        if (this.propertyID) {
            setAPIPropertyID(this.propertyID);
        }

        // Welcome mode state (for toggling between suggested questions and chat)
        this.inWelcomeMode = false;

        // Subscription status (Phase 6: Widget offline state)
        this.subscriptionActive = true;
        this.offlineContactInfo = {};

        // Resolve property before initializing widget
        this.resolveProperty()
            .then(() => this.checkSubscriptionStatus()) // Phase 6: Check if subscription is active
            .then(() => {
                if (this.subscriptionActive) {
                    this.init();
                } else {
                    this.showOfflineState();
                }
            })
            .catch(error => {
                console.error('Failed to resolve property or check subscription:', error);
                // Initialize anyway with no property (backward compatibility)
                this.init();
            });
    }

    async resolveProperty() {
        // Option 1: Explicit property ID in config
        if (this.config.propertyID) {
            this.propertyID = this.config.propertyID;
            // Set API header for all subsequent requests
            setAPIPropertyID(this.propertyID);
            // Storage prefix already set by initializeState()

            // Fetch server config for this property (respects X-Property-ID header)
            await this.fetchServerConfig();
            return;
        }

        // Skip domain resolution in preview mode (admin panel)
        // Property context comes from admin session instead
        if (this.config.previewMode) {
            return;
        }

        // Option 2: Resolve by current domain (use .host to include port)
        const domain = window.location.host;

        try {
            const url = this.baseUrl ? `${this.baseUrl}/api/properties/resolve?domain=${domain}` : `/api/properties/resolve?domain=${domain}`;
            const response = await fetch(url);
            if (response.ok) {
                const data = await response.json();
                if (data.property_id) {
                    this.propertyID = data.property_id;
                    // Set API header for all subsequent requests
                    setAPIPropertyID(this.propertyID);
                    // Update storage key prefix and reload state for this property
                    // This ensures session isolation when propertyID is resolved by domain
                    setStorageKeyPrefix(this.propertyID);
                    const state = initializeState({ propertyID: this.propertyID });
                    Object.assign(this, state);

                    // Fetch server config for this property (including nudge settings)
                    await this.fetchServerConfig();
                }
            }
        } catch (error) {
            console.warn('Failed to resolve property by domain, using default:', error);
        }
    }

    /**
     * Fetch server-side widget configuration for the current property.
     * Merges server config with defaults, but preserves any explicit user overrides.
     * Order: DEFAULT_CONFIG <- serverConfig <- userConfig (user wins)
     */
    async fetchServerConfig() {
        try {
            const cacheBuster = new Date().getTime();
            const configUrl = this.baseUrl
                ? `${this.baseUrl}/api/widget-config/public?_=${cacheBuster}`
                : `/api/widget-config/public?_=${cacheBuster}`;
            const questionsUrl = this.baseUrl
                ? `${this.baseUrl}/api/suggested-questions?_=${cacheBuster}`
                : `/api/suggested-questions?_=${cacheBuster}`;

            // Build headers with X-Property-ID
            const headers = {};
            if (this.propertyID) {
                headers['X-Property-ID'] = this.propertyID;
            }

            // Fetch both widget config and suggested questions in parallel
            const [configResponse, questionsResponse] = await Promise.all([
                fetch(configUrl, { headers }),
                fetch(questionsUrl, { headers })
            ]);

            if (!configResponse.ok) {
                console.warn('Failed to fetch widget config:', configResponse.status);
                return;
            }

            const serverConfig = await configResponse.json();

            // Parse suggested questions if available
            let suggestedQuestions = [];
            if (questionsResponse.ok) {
                const questionsData = await questionsResponse.json();
                suggestedQuestions = (questionsData.questions || []).map(rule => ({
                    id: rule.id,
                    question: rule.suggested_question || rule.question,
                    response: rule.response_type === 'text' ? rule.text_response : null,
                    redirectUrl: rule.response_type === 'redirect' ? rule.redirect_url : null,
                    requireUserDetails: rule.require_user_details || false,
                    captureIntroMessage: rule.capture_intro_message || '',
                    type: rule.type || 'response_rule',
                    flow_id: rule.flow_id || 0
                }));
            }

            // Map server response to config keys
            const mappedServerConfig = {
                apiEndpoint: serverConfig.api_endpoint,
                botName: serverConfig.bot_name,
                botStatus: serverConfig.bot_status,
                welcomeMessage: serverConfig.welcome_message,
                placeholder: serverConfig.placeholder,
                openOnLoad: serverConfig.open_on_load,
                openOnLoadDelay: serverConfig.open_on_load_delay || 0,
                initialDisplayMode: serverConfig.initial_display_mode || 'closed',
                nudgeTitle: serverConfig.nudge_title || 'Need help?',
                nudgeText: serverConfig.nudge_text || 'Chat with our AI assistant',
                nudgeDelay: serverConfig.nudge_delay || 3,
                nudgeDuration: serverConfig.nudge_duration || 0,
                responseSizeLimit: serverConfig.response_size_limit,
                color: serverConfig.color,
                mobileNotificationTitle: serverConfig.mobile_notification_title,
                mobileNotificationText: serverConfig.mobile_notification_text,
                botIcon: serverConfig.bot_icon,
                maxHeightDesktop: serverConfig.max_height_desktop,
                suggestedQuestions: suggestedQuestions
            };

            // Remove undefined values from mapped config (but keep empty arrays)
            Object.keys(mappedServerConfig).forEach(key => {
                if (mappedServerConfig[key] === undefined) {
                    delete mappedServerConfig[key];
                }
            });

            // Merge: DEFAULT_CONFIG <- serverConfig <- userConfig
            // This ensures user's explicit config always wins over server config
            this.config = { ...DEFAULT_CONFIG, ...mappedServerConfig, ...this._userConfig };

            // Update apiEndpoint if it changed (handle relative URLs)
            if (this.config.apiEndpoint && !this.config.apiEndpoint.startsWith('http') && this.baseUrl) {
                this.apiEndpoint = this.baseUrl + this.config.apiEndpoint;
            } else if (this.config.apiEndpoint) {
                this.apiEndpoint = this.config.apiEndpoint;
            }

            // Handle relative bot icon URLs (prepend base URL for cross-origin widgets)
            if (this.config.botIcon && this.config.botIcon.startsWith('/') && this.baseUrl) {
                this.config.botIcon = this.baseUrl + this.config.botIcon;
            }

            // Re-inject styles with updated config (color, maxHeightDesktop, etc.)
            // This is needed because createWidget() runs before fetchServerConfig()
            if (this.shadowRoot) {
                updateStyles(this.shadowRoot, this.config);
                // Also update bot icon in already-rendered elements
                updateBotIconElements(this.shadowRoot, this.config);
                // Update nudge text with server config values
                updateNudgeText(this.shadowRoot, this.config);
            }

        } catch (error) {
            console.warn('Failed to fetch server widget config:', error);
            // Continue with existing config - don't block widget initialization
        }
    }

    async checkSubscriptionStatus() {
        // Skip subscription check in preview mode (admin panel)
        if (this.config.previewMode) {
            this.subscriptionActive = true;
            return;
        }

        try {
            const url = this.baseUrl ? `${this.baseUrl}/api/widget/subscription-status` : `/api/widget/subscription-status`;
            const response = await fetch(url);

            if (response.ok) {
                const data = await response.json();
                this.subscriptionActive = data.active || false;

                // Store contact info if widget is offline
                if (!this.subscriptionActive) {
                    this.offlineContactInfo = {
                        message: data.message || 'This chat is temporarily offline.',
                        email: data.contact_email || null,
                        phone: data.contact_phone || null
                    };
                }
            } else {
                // Default to offline if we can't verify subscription
                this.subscriptionActive = false;
                this.offlineContactInfo = {
                    message: 'This chat is temporarily offline. Please check back soon.',
                    email: null,
                    phone: null
                };
            }
        } catch (error) {
            console.warn('Failed to check subscription status:', error);
            // Default to offline if check fails (fail safely)
            this.subscriptionActive = false;
            this.offlineContactInfo = {
                message: 'This chat is temporarily offline. Please check back soon.',
                email: null,
                phone: null
            };
        }
    }

    showOfflineState() {
        // Render offline message in the widget
        const message = this.offlineContactInfo.message || 'This chat is temporarily offline.';
        const email = this.offlineContactInfo.email;
        const phone = this.offlineContactInfo.phone;

        // Create offline message HTML
        let offlineHTML = `
            <div class="offline-state" style="padding: 20px; text-align: center;">
                <div style="font-size: 48px; margin-bottom: 16px;">💤</div>
                <div style="font-size: 16px; font-weight: 600; color: #374151; margin-bottom: 8px;">
                    Chat Offline
                </div>
                <div style="font-size: 14px; color: #6b7280; margin-bottom: 20px;">
                    ${message}
                </div>
        `;

        // Add contact methods if available
        if (email || phone) {
            offlineHTML += `
                <div style="font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 12px;">
                    Contact Us:
                </div>
            `;

            if (email) {
                offlineHTML += `
                    <div style="margin-bottom: 8px;">
                        <a href="mailto:${email}" style="color: ${this.config.color || '#667eea'}; text-decoration: none; font-size: 14px;">
                            📧 ${email}
                        </a>
                    </div>
                `;
            }

            if (phone) {
                offlineHTML += `
                    <div style="margin-bottom: 8px;">
                        <a href="tel:${phone}" style="color: ${this.config.color || '#667eea'}; text-decoration: none; font-size: 14px;">
                            📞 ${phone}
                        </a>
                    </div>
                `;
            }
        }

        offlineHTML += `</div>`;

        // Add the offline message to the messages container
        this.messagesContainer.innerHTML = offlineHTML;

        // Disable input and send button
        this.input.disabled = true;
        this.input.placeholder = 'Chat is offline';
        this.sendBtn.disabled = true;
    }

    // Translate method (wrapper around imported t function)
    t(errorCode, fallback = null) {
        return t(errorCode, this.language, fallback);
    }

    // Set language method (wrapper)
    setLanguage(langCode) {
        const result = setLanguage(langCode);
        if (result) {
            this.language = result;
        }
    }

    // Translate validation errors
    translateValidationError(message) {
        return translateValidationError(message, this.language);
    }

    init() {
        // Note: init() is now called from resolveProperty() promise chain
        this.attachEventListeners();
        this.initWidgetHeight();
        renderMessages(this.messages, this.messagesContainer, this.config);
        this.scrollToBottom();

        // Initialize session ID if not present
        if (!this.sessionId) {
            this.initializeSession();
        }

        // Show suggested questions on first chat load
        if (this.messages.length === 0 && !this.hasShownSuggestedQuestions && this.config.suggestedQuestions.length > 0) {
            this.showSuggestedQuestionsOrPhoneCapture();
        }

        // Check if we should reopen after a redirect
        // loadReopenAfterRedirect() returns true and clears the flag in one call
        if (loadReopenAfterRedirect()) {
            setTimeout(() => {
                hideMobileNotification(this.mobileNotification);
                hideNudge(this.nudge);
                this.open();
            }, 500);
            return;
        }

        // Handle initial display mode
        const userManuallyClosed = loadManuallyClosed();
        if (userManuallyClosed) {
            // User manually closed widget - don't show anything
            return;
        }

        // Determine display mode - use initialDisplayMode if set, fall back to legacy openOnLoad
        let displayMode = this.config.initialDisplayMode || 'closed';

        // Legacy support: if initialDisplayMode not explicitly set but openOnLoad is true, use 'open'
        if (displayMode === 'closed' && this.config.openOnLoad === true) {
            displayMode = 'open';
        }

        if (displayMode === 'closed') {
            // Widget starts closed - do nothing
            return;
        }

        if (window.innerWidth <= 480) {
            // Mobile: always show mobile notification for nudge or open modes
            setTimeout(() => {
                showMobileNotification(this.mobileNotification);
            }, 300);
        } else {
            // Desktop: handle based on display mode
            if (displayMode === 'nudge') {
                // Show nudge bubble after configured delay
                const delayMs = (this.config.nudgeDelay || 3) * 1000 + 300;
                this.nudgeShowTimer = setTimeout(() => {
                    showNudge(this.nudge);
                    // Auto-dismiss if duration is set
                    if (this.config.nudgeDuration > 0) {
                        this.nudgeAutoDismissTimer = setTimeout(() => {
                            hideNudge(this.nudge, true);
                        }, this.config.nudgeDuration * 1000);
                    }
                }, delayMs);
            } else if (displayMode === 'open') {
                // Open chat widget after configured delay
                const delayMs = (this.config.openOnLoadDelay || 0) * 1000 + 300;
                setTimeout(() => {
                    this.open();
                }, delayMs);
            }
        }
    }

    async initializeSession() {
        const result = await initSession(this.apiEndpoint);
        if (result) {
            this.sessionId = result.sessionId;
            // Capture property_id from server response (needed for sendBeacon which can't send headers)
            if (result.propertyId && !this.propertyID) {
                this.propertyID = result.propertyId;
                setAPIPropertyID(this.propertyID);
                savePropertyId(this.propertyID); // Persist for session restore
            }
        }
    }

    initWidgetHeight() {
        // Skip on mobile - resize is disabled there
        if (window.innerWidth <= 480) {
            return;
        }

        // Restore saved height if available
        const savedHeight = loadWidgetHeight();
        if (savedHeight) {
            this.window.style.height = `${savedHeight}px`;
        }

        // Set up drag behavior for resize handle
        if (!this.resizeHandle) return;

        let isDragging = false;
        let startY = 0;
        let startHeight = 0;

        const onMouseDown = (e) => {
            isDragging = true;
            startY = e.clientY;
            startHeight = this.window.offsetHeight;

            // Prevent text selection during drag
            e.preventDefault();
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'ns-resize';
        };

        const onMouseMove = (e) => {
            if (!isDragging) return;

            // Dragging up (negative deltaY) should increase height
            // Dragging down (positive deltaY) should decrease height
            const deltaY = startY - e.clientY;
            let newHeight = startHeight + deltaY;

            // Clamp to min/max
            const minHeight = 300;
            const maxHeight = Math.min(
                parseInt(this.config.maxHeightDesktop) || 600,
                window.innerHeight - 160
            );
            newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));

            this.window.style.height = `${newHeight}px`;
        };

        const onMouseUp = () => {
            if (!isDragging) return;
            isDragging = false;

            document.body.style.userSelect = '';
            document.body.style.cursor = '';

            // Save the final height
            const finalHeight = this.window.offsetHeight;
            if (finalHeight >= 300) {
                saveWidgetHeight(finalHeight);
            }
        };

        this.resizeHandle.addEventListener('mousedown', onMouseDown);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        // Touch support for tablets
        this.resizeHandle.addEventListener('touchstart', (e) => {
            const touch = e.touches[0];
            onMouseDown({ clientY: touch.clientY, preventDefault: () => e.preventDefault() });
        }, { passive: false });

        document.addEventListener('touchmove', (e) => {
            if (!isDragging) return;
            const touch = e.touches[0];
            onMouseMove({ clientY: touch.clientY });
        }, { passive: true });

        document.addEventListener('touchend', onMouseUp);
    }

    attachEventListeners() {
        this.toggleBtn.addEventListener('click', () => this.toggle());
        this.closeBtn.addEventListener('click', () => this.close());
        this.backBtn.addEventListener('click', () => this.returnToWelcomeMode());
        this.sendBtn.addEventListener('click', () => this.sendMessage());

        this.input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.sendMessage();
            }
        });

        this.input.addEventListener('input', () => {
            this.input.style.height = 'auto';
            this.input.style.height = Math.min(this.input.scrollHeight, 100) + 'px';
        });

        this.input.addEventListener('input', () => {
            this.sendBtn.disabled = this.input.value.trim() === '';
        });

        // Mobile notification listeners
        this.openFromNotifBtn.addEventListener('click', () => {
            hideMobileNotification(this.mobileNotification);
            this.open();
        });

        this.dismissNotifBtn.addEventListener('click', () => {
            hideMobileNotification(this.mobileNotification);
        });

        // Desktop nudge listeners
        this.openFromNudgeBtn.addEventListener('click', () => {
            if (this.nudgeAutoDismissTimer) {
                clearTimeout(this.nudgeAutoDismissTimer);
            }
            hideNudge(this.nudge);
            this.open();
        });

        this.dismissNudgeBtn.addEventListener('click', () => {
            if (this.nudgeAutoDismissTimer) {
                clearTimeout(this.nudgeAutoDismissTimer);
            }
            hideNudge(this.nudge, true);
        });

        // Save chat log on page unload (for monitoring during soft launch)
        // Skip in preview mode (admin panel) since those are test chats
        if (!this.config.previewMode) {
            this._chatSaved = false;
            window.addEventListener('beforeunload', () => {
                this.saveChatLog('page_close');
            });
            // Also save on pagehide for mobile browsers
            window.addEventListener('pagehide', () => {
                this.saveChatLog('page_close');
            });
        }
    }

    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    open() {
        this.isOpen = true;
        // Cancel pending nudge show timer and hide nudge if visible
        if (this.nudgeShowTimer) {
            clearTimeout(this.nudgeShowTimer);
            this.nudgeShowTimer = null;
        }
        if (this.nudge) {
            if (this.nudgeAutoDismissTimer) {
                clearTimeout(this.nudgeAutoDismissTimer);
            }
            hideNudge(this.nudge);
        }
        open(this.window, this.input);
        scrollToBottom(this.messagesContainer);
    }

    close() {
        this.isOpen = false;
        close(this.window);
    }

    scrollToBottom() {
        scrollToBottom(this.messagesContainer);
    }

    // Save chat log for monitoring (soft launch feature)
    // Called on page unload or when conversation explicitly ends
    saveChatLog(endReason = 'page_close') {
        // Only save once per session
        if (this._chatSaved) {
            return;
        }

        // Only save if there are actual messages
        if (!this.messages || this.messages.length === 0) {
            return;
        }

        // Only save if we have a session ID
        if (!this.sessionId) {
            return;
        }

        this._chatSaved = true;
        saveChatLogAPI(this.baseUrl, this.sessionId, endReason, this.propertyID);
    }

    // Switch to chat mode (hide welcome, show back button)
    enterChatMode() {
        // Don't enable this in preview mode (admin panel)
        if (this.config.previewMode) {
            return;
        }

        this.inWelcomeMode = false;
        hideSuggestedQuestions(this.messagesContainer);
        showBackButton(this.backBtn, this.header);
    }

    // Return to welcome mode (show suggested questions, hide back button)
    returnToWelcomeMode() {
        // Don't enable this in preview mode (admin panel)
        if (this.config.previewMode) {
            return;
        }

        this.inWelcomeMode = true;

        // Remove all chat messages (not just hide - we're starting fresh)
        const allMessages = this.messagesContainer.querySelectorAll('.chat-message');
        allMessages.forEach(msg => {
            msg.remove();
        });

        // Remove any forms
        const forms = this.messagesContainer.querySelectorAll('.user-details-form, #appointmentForm, .user-details-summary');
        forms.forEach(form => {
            form.remove();
        });

        // Clear session so next question starts a new conversation
        this.sessionId = '';
        saveSessionId('');
        this.messages = [];
        saveMessages([]);

        // Show suggested questions if they exist, otherwise re-create them
        const questionsShown = showSuggestedQuestionsIfHidden(this.messagesContainer);
        if (!questionsShown && this.config.suggestedQuestions.length > 0) {
            showSuggestedQuestions(
                this.messagesContainer,
                this.config.suggestedQuestions,
                (question, flowId) => {
                    this.handleSuggestedQuestionClick(question, flowId);
                }
            );
        }

        // Hide back button, show full header
        hideBackButton(this.backBtn, this.header);
    }

    // Handle suggested question click
    handleSuggestedQuestionClick(question, flowId = 0) {
        this.hasShownSuggestedQuestions = true;

        // Enter chat mode
        this.enterChatMode();

        // Send the message with optional flow_id for deterministic triggering
        this.sendMessage(question, flowId);
    }

    addMessage(role, content) {
        const messageElement = addMsg(role, content, this.messages, this.messagesContainer, this.config);
        return messageElement;
    }

    addChoiceButtons(messageElement, choices) {
        // Add choice buttons to the message with a callback to handle clicks
        // Callback receives (key, label) - key for server, label for display
        addChoiceButtonsToMessage(messageElement, choices, (selectedKey, displayLabel) => {
            // When a choice button is clicked:
            // - Display the label (what user clicked, possibly translated) as user message
            // - Send the key (original English) to the server for matching
            this.sendMessageWithDisplay(selectedKey, displayLabel);
        });
    }

    showTypingIndicator() {
        if (!this.isTyping) {
            this.isTyping = true;
            showTypingIndicator(this.shadowRoot, this.messagesContainer, this.config);
            scrollToBottom(this.messagesContainer);
        }
    }

    hideTypingIndicator() {
        this.isTyping = false;
        hideTypingIndicator(this.shadowRoot);
    }

    showError(message) {
        showError(this.messagesContainer, message);
    }

    async handleAPIError(response, fallbackMessageCode = 'GENERIC_ERROR') {
        return await handleAPIError(response, this.messagesContainer, this.language, fallbackMessageCode);
    }

    async sendMessage(providedMessage = null, flowId = 0) {
        const message = providedMessage || this.input.value.trim();
        if (!message) return;

        if (!providedMessage) {
            this.input.value = '';
            this.input.style.height = 'auto';
        }
        this.sendBtn.disabled = true;

        this.addMessage('user', message);
        this.showTypingIndicator();

        try {
            const data = await sendQuery(this.apiEndpoint, message, this.sessionId, this.config.responseSizeLimit, flowId);

            this.hideTypingIndicator();

            if (data.session_id) {
                this.sessionId = data.session_id;
                saveSessionId(this.sessionId);
            }

            // Sync widget language with server's detected language
            if (data.detected_language && data.detected_language !== this.language) {
                this.language = data.detected_language;
                setLanguage(data.detected_language);
            }

            // Strip unknown markers and translate validation errors
            if (data.answer) {
                data.answer = stripUnknownMarkers(data.answer);
                data.answer = this.translateValidationError(data.answer);
            }

            // Handle redirect if present
            if (data.redirect_url) {
                if (data.answer && data.answer.trim() !== '') {
                    this.addMessage('assistant', data.answer);
                }
                this.showRedirectConfirmation(data.redirect_url);
            } else {
                this.processMarkers(data, message);
            }

            this.sendBtn.disabled = false;

        } catch (error) {
            this.hideTypingIndicator();
            console.error('Error sending message:', error);

            // Handle network errors with retry logic
            const isNetworkError = error instanceof TypeError &&
                (error.message.includes('fetch') ||
                    error.message.includes('network') ||
                    error.message.includes('Failed to fetch'));

            if (isNetworkError && this.sessionId && !this.hasRetriedAfterError) {
                this.hasRetriedAfterError = true;
                this.sessionId = null;
                sessionStorage.removeItem('chat_widget_session_id');
                this.addMessage('system', 'Connection restored. Starting a new session...');
                this.sendMessage(message);
            } else {
                this.showError(this.t('GENERIC_ERROR'));
                this.hasRetriedAfterError = false;
            }
        }

        this.hasRetriedAfterError = false;
    }

    // Send a message where the displayed text differs from what's sent to server
    // Used for translated choice buttons: display translated label, send original key
    async sendMessageWithDisplay(queryMessage, displayMessage) {
        if (!queryMessage) return;

        this.sendBtn.disabled = true;

        // Display the translated label to the user
        this.addMessage('user', displayMessage);
        this.showTypingIndicator();

        try {
            // Send the original key to the server for matching
            const data = await sendQuery(this.apiEndpoint, queryMessage, this.sessionId, this.config.responseSizeLimit);

            this.hideTypingIndicator();

            if (data.session_id) {
                this.sessionId = data.session_id;
                saveSessionId(this.sessionId);
            }

            // Sync widget language with server's detected language
            if (data.detected_language && data.detected_language !== this.language) {
                this.language = data.detected_language;
                setLanguage(data.detected_language);
            }

            // Strip unknown markers and translate validation errors
            if (data.answer) {
                data.answer = stripUnknownMarkers(data.answer);
                data.answer = this.translateValidationError(data.answer);
            }

            // Handle redirect if present
            if (data.redirect_url) {
                if (data.answer && data.answer.trim() !== '') {
                    this.addMessage('assistant', data.answer);
                }
                this.showRedirectConfirmation(data.redirect_url);
            } else {
                this.processMarkers(data, queryMessage);
            }

            this.sendBtn.disabled = false;

        } catch (error) {
            this.hideTypingIndicator();
            console.error('Error sending message:', error);
            this.showError(this.t('GENERIC_ERROR'));
        }
    }

    processMarkers(data, originalMessage) {
        // Check for both CAPTURE_DETAILS and CAPTURE_APPOINTMENT markers
        if (data.answer && hasMarker(data.answer, 'CAPTURE_DETAILS') && hasMarker(data.answer, 'CAPTURE_APPOINTMENT')) {
            let cleanAnswer = data.answer.replace(/<CAPTURE_DETAILS\s*\/?>/gi, '').replace(/<CAPTURE_APPOINTMENT\s*\/?>/gi, '').trim();
            this.pendingCaptureDetailsResponse = cleanAnswer;
            this.pendingCaptureAppointmentResponse = cleanAnswer;

            if (!this.userDetailsCaptured) {
                let introMessageElement = null;
                if (cleanAnswer) {
                    introMessageElement = this.addMessage('assistant', cleanAnswer);
                }
                this.showUserDetailsFormForRecapture(introMessageElement);
            } else {
                this.addMessage('assistant', cleanAnswer);
                this.showAppointmentForm();
            }
        }
        // Check for CAPTURE_APPOINTMENT marker only
        else if (data.answer && hasMarker(data.answer, 'CAPTURE_APPOINTMENT')) {
            const cleanAnswer = data.answer.replace(/<CAPTUR[E]?_?APPOINTMENT\s*\/?>/gi, '').trim();
            this.pendingCaptureAppointmentResponse = cleanAnswer;
            if (cleanAnswer) {
                this.addMessage('assistant', cleanAnswer);
            }
            this.showAppointmentForm();
        }
        // Check for both CAPTURE_DETAILS and CAPTURE_CONVERSATION markers
        // BUT: Response rules should never use <CAPTURE_CONVERSATION />, only conversation flows
        else if (data.answer && hasMarker(data.answer, 'CAPTURE_DETAILS') && hasMarker(data.answer, 'CAPTURE_CONVERSATION')) {
            // Check if this came from a response rule (which is not allowed)
            const ruleIdMatch = data.answer.match(/<RULE_ID\s+id=["'](\d+)["']\s*\/?>/i);
            if (ruleIdMatch) {
                console.warn('Response rule attempted to use <CAPTURE_CONVERSATION /> marker - ignoring');
                // Treat as regular CAPTURE_DETAILS without conversation
                const cleanAnswer = data.answer.replace(/<CAPTUR[E]?_?DETAILS\s*\/?>/gi, '').replace(/<CAPTUR[E]?_?CONVERSATION\s*\/?>/gi, '').replace(/<RULE_ID\s+id=["']\d+["']\s*\/?>/gi, '').trim();
                this.pendingCaptureDetailsResponse = cleanAnswer;
                if (cleanAnswer) {
                    this.addMessage('assistant', cleanAnswer);
                }
                this.showUserDetailsFormForRecapture();
            } else {
                // Normal conversation flow - process both markers
                let cleanAnswer = data.answer.replace(/<CAPTUR[E]?_?DETAILS\s*\/?>/gi, '').replace(/<CAPTUR[E]?_?CONVERSATION\s*\/?>/gi, '').trim();
                this.pendingCaptureDetailsResponse = cleanAnswer;
                this.pendingSendConversationResponse = cleanAnswer;

                if (!this.userDetailsCaptured) {
                    this.showUserDetailsFormForRecapture(null);
                } else {
                    this.sendConversationEmail();
                }
            }
        }
        // Check for CAPTURE_CONVERSATION marker only
        else if (data.answer && hasMarker(data.answer, 'CAPTURE_CONVERSATION')) {
            // Check if this came from a response rule (which is not allowed)
            const ruleIdMatch = data.answer.match(/<RULE_ID\s+id=["'](\d+)["']\s*\/?>/i);
            if (ruleIdMatch) {
                console.warn('Response rule attempted to use <CAPTURE_CONVERSATION /> marker - ignoring');
                // Show the response without processing the marker
                const cleanAnswer = data.answer.replace(/<CAPTUR[E]?_?CONVERSATION\s*\/?>/gi, '').replace(/<RULE_ID\s+id=["']\d+["']\s*\/?>/gi, '').trim();
                if (cleanAnswer) {
                    this.addMessage('assistant', cleanAnswer);
                }
            } else {
                // Normal conversation flow - process the marker
                const cleanAnswer = data.answer.replace(/<CAPTUR[E]?_?CONVERSATION\s*\/?>/gi, '').trim();
                this.pendingSendConversationResponse = cleanAnswer;
                this.sendConversationEmail();
            }
        }
        // Check for CAPTURE_DETAILS + TEXT_RESPONSE (response rule with user details)
        else if (data.answer && hasMarker(data.answer, 'CAPTURE_DETAILS') && hasMarker(data.answer, 'TEXT_RESPONSE')) {
            // Split on the CAPTURE_DETAILS marker to get intro message
            const parts = data.answer.split(/<CAPTURE_DETAILS\s*\/?>/i);
            let introMessage = parts[0].trim();

            const textResponseMatch = data.answer.match(/<TEXT_RESPONSE\s+text=["']([^"']+)["']\s*\/?>/i);
            const textResponse = textResponseMatch ? textResponseMatch[1] : '';
            const ruleIdMatch = data.answer.match(/<RULE_ID\s+id=["'](\d+)["']\s*\/?>/i);
            const ruleId = ruleIdMatch ? parseInt(ruleIdMatch[1], 10) : null;

            this.pendingTextResponse = textResponse;
            this.pendingRuleId = ruleId;

            if (introMessage) {
                this.addMessage('assistant', introMessage);
            }

            if (!this.userDetailsCaptured) {
                this.showUserDetailsFormForRecapture();
            } else {
                this.showUserDetailsConfirmation();
            }
        }
        // Check for CAPTURE_DETAILS marker only
        else if (data.answer && hasMarker(data.answer, 'CAPTURE_DETAILS')) {
            const cleanAnswer = data.answer.replace(/<CAPTURE_DETAILS\s*\/?>/gi, '').trim();
            this.pendingCaptureDetailsResponse = cleanAnswer;
            // Don't resend after capture if contact form was auto-injected by backend
            if (!data.contact_injected) {
                this.pendingCaptureDetailsMessage = originalMessage;
            }

            if (cleanAnswer) {
                this.addMessage('assistant', cleanAnswer);
            }

            this.showUserDetailsFormForRecapture();
        }
        // Check for CAPTURE_EMAIL marker only
        else if (data.answer && hasMarker(data.answer, 'CAPTURE_EMAIL')) {
            const cleanAnswer = data.answer.replace(/<CAPTURE_EMAIL\s*\/?>/gi, '').trim();
            this.pendingCaptureEmailResponse = cleanAnswer;
            this.pendingCaptureEmailMessage = originalMessage;

            if (cleanAnswer) {
                this.addMessage('assistant', cleanAnswer);
            }

            this.showEmailForm();
        }
        // Check for CAPTURE_PHONE marker only
        else if (data.answer && hasMarker(data.answer, 'CAPTURE_PHONE')) {
            const cleanAnswer = data.answer.replace(/<CAPTURE_PHONE\s*\/?>/gi, '').trim();
            this.pendingCapturePhoneResponse = cleanAnswer;
            this.pendingCapturePhoneMessage = originalMessage;

            if (cleanAnswer) {
                this.addMessage('assistant', cleanAnswer);
            }

            this.showPhoneForm();
        }
        // Check for CONTACT_US_FORM marker
        else if (data.answer && hasMarker(data.answer, 'CONTACT_US_FORM')) {
            const cleanAnswer = data.answer.replace(/<CONTACT_US_FORM\s*\/?>/gi, '').trim();

            if (cleanAnswer) {
                this.addMessage('assistant', cleanAnswer);
            }

            this.showContactUsForm();
        } else {
            // Only add message if answer is not empty
            if (data.answer && data.answer.trim() !== '') {
                const messageElement = this.addMessage('assistant', data.answer);

                // Handle multiple choice options from state machine
                // Prefer choice_options (new format with translations) over choices (deprecated)
                const choicesToShow = data.choice_options && data.choice_options.length > 0
                    ? data.choice_options
                    : (data.choices && data.choices.length > 0 ? data.choices : null);
                if (choicesToShow) {
                    this.addChoiceButtons(messageElement, choicesToShow);
                }
            }
        }
    }

    showUserDetailsForm(introMessageElement = null) {
        const formHTML = getUserDetailsFormHTML(this.config, this.t.bind(this));
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = formHTML;
        this.messagesContainer.appendChild(tempDiv.firstElementChild);

        scrollToShowRecentMessages(this.messagesContainer, introMessageElement);

        setupUserDetailsFormListeners(this.shadowRoot);

        const submitBtn = this.shadowRoot.getElementById('submitUserDetails');
        const declineBtn = this.shadowRoot.getElementById('declineUserDetails');

        submitBtn.addEventListener('click', () => this.submitUserDetails());
        declineBtn.addEventListener('click', () => this.declineUserDetails());
    }

    showUserDetailsFormForRecapture(introMessageElement = null) {
        const existingForm = this.shadowRoot.getElementById('userDetailsForm');
        const existingConfirmation = this.shadowRoot.getElementById('userDetailsConfirmation');
        if (existingForm || existingConfirmation) {
            return;
        }

        if (this.userDetails) {
            this.showUserDetailsConfirmation();
        } else {
            this.showUserDetailsForm(introMessageElement);
        }
    }

    showUserDetailsConfirmation() {
        const html = getUserDetailsConfirmationHTML(this.userDetails, this.t.bind(this));
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        this.messagesContainer.appendChild(tempDiv.firstElementChild);
        scrollToBottom(this.messagesContainer);

        const confirmBtn = this.shadowRoot.getElementById('confirmUserDetails');
        const updateBtn = this.shadowRoot.getElementById('updateUserDetails');

        confirmBtn.addEventListener('click', () => this.confirmExistingDetails());
        updateBtn.addEventListener('click', () => this.updateExistingDetails());
    }

    async confirmExistingDetails() {
        const confirmation = this.shadowRoot.getElementById('userDetailsConfirmation');
        if (confirmation) confirmation.remove();

        try {
            // Ensure we have a session ID
            if (!this.sessionId) {
                const initResponse = await sendQuery(this.apiEndpoint, "init", "");
                if (initResponse.session_id) {
                    this.sessionId = initResponse.session_id;
                    saveSessionId(this.sessionId);
                }
            }

            // Prepare chat history
            const chatHistory = this.messages.map(msg =>
                `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`
            ).join('\n');

            // Try to submit existing user details to API
            // This may fail with DUPLICATE_REQUEST if already submitted in this session,
            // which is expected and acceptable when confirming existing details
            let captureResult = null;
            try {
                captureResult = await submitUserDetailsAPI(
                    this.baseUrl,
                    this.sessionId,
                    {
                        chatHistory,
                        firstName: this.userDetails.firstName,
                        lastName: this.userDetails.lastName,
                        phone: this.userDetails.phone,
                        email: this.userDetails.email,
                        consent: this.userDetails.consent || false
                    },
                    this.pendingRuleId
                );
            } catch (apiError) {
                // Check if this is a DUPLICATE_REQUEST error (already submitted in this session)
                // If so, this is expected and we can continue - user details are already on file
                let isDuplicate = false;
                try {
                    const errorData = await apiError.json();
                    if (errorData.error === 'DUPLICATE_REQUEST') {
                        isDuplicate = true;
                    }
                } catch (e) {
                    // Failed to parse error, will handle below
                }

                // If it's not a duplicate error, throw it to be handled by outer catch
                if (!isDuplicate) {
                    throw apiError;
                }
            }

            // Show submitted summary in chat
            const submittedHTML = getUserDetailsSubmittedHTML(this.userDetails, this.t.bind(this));
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = submittedHTML;
            this.messagesContainer.appendChild(tempDiv.firstElementChild);
            scrollToBottom(this.messagesContainer);

            // Show notification delivery feedback
            if (captureResult?.notification_sent === true) {
                this.addMessage('system', this.t('NOTIFICATION_SENT'));
            } else if (captureResult?.notification_sent === false) {
                this.addMessage('system', this.t('NOTIFICATION_FAILED'));
            }

        } catch (error) {
            console.error('Error confirming user details:', error);
            await this.handleAPIError(error, 'NETWORK_ERROR');
            return;
        }

        // Handle pending message resend (to continue conversation flow)
        if (this.pendingCaptureDetailsMessage) {
            const message = this.pendingCaptureDetailsMessage;
            this.pendingCaptureDetailsMessage = null;
            this.pendingCaptureDetailsResponse = null; // Clear this too

            this.showTypingIndicator();
            sendQuery(this.apiEndpoint, message, this.sessionId, this.config.responseSizeLimit)
                .then(data => {
                    this.hideTypingIndicator();
                    if (data.session_id) {
                        this.sessionId = data.session_id;
                        saveSessionId(this.sessionId);
                    }
                    // Use processMarkers for full marker handling (strips capture markers, handles redirects, etc.)
                    this.processMarkers(data, message);
                })
                .catch(error => {
                    this.hideTypingIndicator();
                    this.addMessage('system', 'Sorry, there was an error processing your request.');
                    console.error('Error sending query:', error);
                });
            return;
        }

        if (this.pendingTextResponse !== null) {
            this.addMessage('assistant', this.pendingTextResponse);
            this.pendingTextResponse = null;
            this.pendingCaptureDetailsResponse = null;
            return;
        }

        if (this.pendingCaptureAppointmentResponse !== null) {
            if (this.pendingCaptureAppointmentResponse) {
                this.addMessage('assistant', this.pendingCaptureAppointmentResponse);
            }
            this.pendingCaptureDetailsResponse = null;
            this.showAppointmentForm();
            return;
        }

        if (this.pendingSendConversationResponse !== null) {
            this.pendingCaptureDetailsResponse = null;
            this.sendConversationEmail();
            return;
        }

        // Clear the pending response (it was already shown before the confirmation)
        this.pendingCaptureDetailsResponse = null;
    }

    updateExistingDetails() {
        const confirmation = this.shadowRoot.getElementById('userDetailsConfirmation');
        if (confirmation) confirmation.remove();
        this.showUserDetailsForm();
    }

    // Email form methods
    showEmailForm(introMessageElement = null, forceUpdate = false) {
        // Check if form already exists
        const existingForm = this.shadowRoot.getElementById('emailForm');
        const existingConfirmation = this.shadowRoot.getElementById('emailConfirmation');
        if (existingForm || existingConfirmation) {
            return;
        }

        // If we already have email, show confirmation instead (unless forcing update)
        if (!forceUpdate && this.userDetails && this.userDetails.email) {
            this.showEmailConfirmation();
            return;
        }

        const formHTML = getEmailFormHTML(this.config, this.t.bind(this));
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = formHTML;
        this.messagesContainer.appendChild(tempDiv.firstElementChild);

        scrollToShowRecentMessages(this.messagesContainer, introMessageElement);

        const submitBtn = this.shadowRoot.getElementById('submitEmail');
        const declineBtn = this.shadowRoot.getElementById('declineEmail');

        if (submitBtn && declineBtn) {
            submitBtn.addEventListener('click', () => this.submitEmail());
            declineBtn.addEventListener('click', () => this.declineEmail());
        } else {
            console.error('Email form buttons not found:', { submitBtn, declineBtn });
        }
    }

    showEmailConfirmation() {
        const fullName = `${this.userDetails.firstName || ''} ${this.userDetails.lastName || ''}`.trim() || 'Not provided';
        const html = getEmailConfirmationHTML(fullName, this.userDetails.email, this.t.bind(this));
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        this.messagesContainer.appendChild(tempDiv.firstElementChild);
        scrollToBottom(this.messagesContainer);

        const confirmBtn = this.shadowRoot.getElementById('confirmEmail');
        const updateBtn = this.shadowRoot.getElementById('updateEmail');

        if (confirmBtn && updateBtn) {
            confirmBtn.addEventListener('click', () => this.confirmExistingEmail());
            updateBtn.addEventListener('click', () => this.updateExistingEmail());
        } else {
            console.error('Email confirmation buttons not found:', { confirmBtn, updateBtn });
        }
    }

    async confirmExistingEmail() {
        const confirmation = this.shadowRoot.getElementById('emailConfirmation');
        if (confirmation) confirmation.remove();

        // Resend the pending message to continue the flow
        if (this.pendingCaptureEmailMessage) {
            const message = this.pendingCaptureEmailMessage;
            this.pendingCaptureEmailMessage = null;
            this.pendingCaptureEmailResponse = null;

            this.showTypingIndicator();
            sendQuery(this.apiEndpoint, message, this.sessionId, this.config.responseSizeLimit)
                .then(data => {
                    this.hideTypingIndicator();
                    if (data.session_id) {
                        this.sessionId = data.session_id;
                        saveSessionId(this.sessionId);
                    }
                    this.processMarkers(data, message);
                })
                .catch(error => {
                    this.hideTypingIndicator();
                    this.addMessage('system', 'Sorry, there was an error processing your request.');
                    console.error('Error sending query:', error);
                });
        } else {
            this.pendingCaptureEmailResponse = null;
        }
    }

    updateExistingEmail() {
        const confirmation = this.shadowRoot.getElementById('emailConfirmation');
        if (confirmation) confirmation.remove();
        this.showEmailForm(null, true); // Force showing the form even if email exists
    }

    async submitEmail() {
        const nameInput = this.shadowRoot.getElementById('userName');
        const emailInput = this.shadowRoot.getElementById('userEmail');
        const consent = this.shadowRoot.getElementById('userConsent');
        const nameError = this.shadowRoot.getElementById('nameError');
        const emailError = this.shadowRoot.getElementById('emailError');

        const name = nameInput.value.trim();
        const email = emailInput.value.trim();

        // Reset errors
        nameError.style.display = 'none';
        emailError.style.display = 'none';
        nameInput.classList.remove('error');
        emailInput.classList.remove('error');

        // Validate name
        if (!name) {
            nameError.textContent = 'Name is required';
            nameError.style.display = 'block';
            nameInput.classList.add('error');
            return;
        }

        // Validate email
        if (!email) {
            emailError.textContent = 'Email is required';
            emailError.style.display = 'block';
            emailInput.classList.add('error');
            return;
        }

        if (!validateEmail(email)) {
            emailError.textContent = 'Please enter a valid email';
            emailError.style.display = 'block';
            emailInput.classList.add('error');
            return;
        }

        if (!consent.checked) {
            emailError.textContent = this.t('FORM_CONSENT_REQUIRED');
            emailError.style.display = 'block';
            return;
        }

        const submitBtn = this.shadowRoot.getElementById('submitEmail');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending...';

        try {
            const chatHistory = this.messages.map(msg =>
                `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`
            ).join('\n');

            // Split name into firstName and lastName
            const nameParts = name.split(/\s+/);
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            const captureResult = await submitEmailAPI(
                this.baseUrl,
                this.sessionId,
                {
                    chatHistory,
                    firstName,
                    lastName,
                    email,
                    consent: consent.checked
                }
            );

            // Save email and name
            this.userDetails = { ...this.userDetails, firstName, lastName, email, consent: consent.checked };
            saveUserDetails(this.userDetails);

            // Remove form
            const form = this.shadowRoot.getElementById('emailForm');
            if (form) form.remove();

            // Show notification delivery feedback
            if (captureResult?.notification_sent === true) {
                this.addMessage('system', this.t('NOTIFICATION_SENT'));
            } else if (captureResult?.notification_sent === false) {
                this.addMessage('system', this.t('NOTIFICATION_FAILED'));
            }

            // Resend the pending message to continue the flow
            if (this.pendingCaptureEmailMessage) {
                const message = this.pendingCaptureEmailMessage;
                this.pendingCaptureEmailMessage = null;
                this.pendingCaptureEmailResponse = null;

                this.showTypingIndicator();
                sendQuery(this.apiEndpoint, message, this.sessionId, this.config.responseSizeLimit)
                    .then(data => {
                        this.hideTypingIndicator();
                        if (data.session_id) {
                            this.sessionId = data.session_id;
                            saveSessionId(this.sessionId);
                        }
                        this.processMarkers(data, message);
                    })
                    .catch(error => {
                        this.hideTypingIndicator();
                        this.addMessage('system', 'Sorry, there was an error processing your request.');
                        console.error('Error sending query:', error);
                    });
            } else {
                this.pendingCaptureEmailResponse = null;
            }

        } catch (error) {
            console.error('Error submitting email:', error);
            emailError.textContent = 'Failed to submit email. Please try again.';
            emailError.style.display = 'block';
            submitBtn.disabled = false;
            submitBtn.textContent = 'Send';
        }
    }

    async declineEmail() {
        const form = this.shadowRoot.getElementById('emailForm');
        if (form) form.remove();

        this.pendingCaptureEmailResponse = null;
        this.pendingCaptureEmailMessage = null;

        // Send decline signal to backend to allow flow transition
        this.showTypingIndicator();
        try {
            const data = await sendQuery(this.apiEndpoint, '_decline_', this.sessionId, this.config.responseSizeLimit);
            this.hideTypingIndicator();

            if (data.session_id) {
                this.sessionId = data.session_id;
                saveSessionId(this.sessionId);
            }

            // If backend handled the decline (has answer or markers), process it
            if (data.answer || data.redirect_url) {
                if (data.redirect_url) {
                    this.showRedirectConfirmation(data.redirect_url);
                } else {
                    this.processMarkers(data, '_decline_');
                }
            }
            // If backend didn't provide a response, no message is shown - user can continue chatting
        } catch (error) {
            this.hideTypingIndicator();
            console.error('Error sending decline signal:', error);
            // On error, silently continue - user can still chat
        }
    }

    // Phone form methods
    showPhoneForm(introMessageElement = null, forceUpdate = false) {
        // Check if form already exists
        const existingForm = this.shadowRoot.getElementById('phoneForm');
        const existingConfirmation = this.shadowRoot.getElementById('phoneConfirmation');
        if (existingForm || existingConfirmation) {
            return;
        }

        // If we already have phone, show confirmation instead (unless forcing update)
        if (!forceUpdate && this.userDetails && this.userDetails.phone) {
            this.showPhoneConfirmation();
            return;
        }

        const formHTML = getPhoneFormHTML(this.config, this.t.bind(this));
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = formHTML;
        this.messagesContainer.appendChild(tempDiv.firstElementChild);

        scrollToShowRecentMessages(this.messagesContainer, introMessageElement);

        setupPhoneFormListeners(this.shadowRoot);

        const submitBtn = this.shadowRoot.getElementById('submitPhone');
        const declineBtn = this.shadowRoot.getElementById('declinePhone');

        if (submitBtn && declineBtn) {
            submitBtn.addEventListener('click', () => this.submitPhone());
            declineBtn.addEventListener('click', () => this.declinePhone());
        } else {
            console.error('Phone form buttons not found:', { submitBtn, declineBtn });
        }
    }

    showPhoneConfirmation() {
        const fullName = `${this.userDetails.firstName || ''} ${this.userDetails.lastName || ''}`.trim() || 'Not provided';
        const html = getPhoneConfirmationHTML(fullName, this.userDetails.phone, this.t.bind(this));
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        this.messagesContainer.appendChild(tempDiv.firstElementChild);
        scrollToBottom(this.messagesContainer);

        const confirmBtn = this.shadowRoot.getElementById('confirmPhone');
        const updateBtn = this.shadowRoot.getElementById('updatePhone');

        if (confirmBtn && updateBtn) {
            confirmBtn.addEventListener('click', () => this.confirmExistingPhone());
            updateBtn.addEventListener('click', () => this.updateExistingPhone());
        } else {
            console.error('Phone confirmation buttons not found:', { confirmBtn, updateBtn });
        }
    }

    async confirmExistingPhone() {
        const confirmation = this.shadowRoot.getElementById('phoneConfirmation');
        if (confirmation) confirmation.remove();

        // Resend the pending message to continue the flow
        if (this.pendingCapturePhoneMessage) {
            const message = this.pendingCapturePhoneMessage;
            this.pendingCapturePhoneMessage = null;
            this.pendingCapturePhoneResponse = null;

            this.showTypingIndicator();
            sendQuery(this.apiEndpoint, message, this.sessionId, this.config.responseSizeLimit)
                .then(data => {
                    this.hideTypingIndicator();
                    if (data.session_id) {
                        this.sessionId = data.session_id;
                        saveSessionId(this.sessionId);
                    }
                    this.processMarkers(data, message);
                })
                .catch(error => {
                    this.hideTypingIndicator();
                    this.addMessage('system', 'Sorry, there was an error processing your request.');
                    console.error('Error sending query:', error);
                });
        } else {
            this.pendingCapturePhoneResponse = null;
        }
    }

    updateExistingPhone() {
        const confirmation = this.shadowRoot.getElementById('phoneConfirmation');
        if (confirmation) confirmation.remove();
        this.showPhoneForm(null, true); // Force showing the form even if phone exists
    }

    async submitPhone() {
        const nameInput = this.shadowRoot.getElementById('userName');
        const phoneInput = this.shadowRoot.getElementById('userPhone');
        const consent = this.shadowRoot.getElementById('userConsent');
        const nameError = this.shadowRoot.getElementById('nameError');
        const phoneError = this.shadowRoot.getElementById('phoneError');

        const name = nameInput.value.trim();
        const phone = phoneInput.value.trim();

        // Reset errors
        nameError.style.display = 'none';
        phoneError.style.display = 'none';
        nameInput.classList.remove('error');
        phoneInput.classList.remove('error');

        // Validate name
        if (!name) {
            nameError.textContent = 'Name is required';
            nameError.style.display = 'block';
            nameInput.classList.add('error');
            return;
        }

        // Validate phone
        if (!phone) {
            phoneError.textContent = 'Phone is required';
            phoneError.style.display = 'block';
            phoneInput.classList.add('error');
            return;
        }

        if (!validatePhoneNumber(phone)) {
            phoneError.textContent = 'Please enter a valid phone number';
            phoneError.style.display = 'block';
            phoneInput.classList.add('error');
            return;
        }

        if (!consent.checked) {
            phoneError.textContent = this.t('FORM_CONSENT_REQUIRED');
            phoneError.style.display = 'block';
            return;
        }

        const submitBtn = this.shadowRoot.getElementById('submitPhone');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending...';

        try {
            const chatHistory = this.messages.map(msg =>
                `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`
            ).join('\n');

            const formattedPhone = formatPhoneNumber(phone);

            // Split name into firstName and lastName
            const nameParts = name.split(/\s+/);
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            const captureResult = await submitPhoneAPI(
                this.baseUrl,
                this.sessionId,
                {
                    chatHistory,
                    firstName,
                    lastName,
                    phone: formattedPhone,
                    consent: consent.checked
                }
            );

            // Save phone and name
            this.userDetails = { ...this.userDetails, firstName, lastName, phone: formattedPhone, consent: consent.checked };
            saveUserDetails(this.userDetails);

            // Remove form
            const form = this.shadowRoot.getElementById('phoneForm');
            if (form) form.remove();

            // Show notification delivery feedback
            if (captureResult?.notification_sent === true) {
                this.addMessage('system', this.t('NOTIFICATION_SENT'));
            } else if (captureResult?.notification_sent === false) {
                this.addMessage('system', this.t('NOTIFICATION_FAILED'));
            }

            // Resend the pending message to continue the flow
            if (this.pendingCapturePhoneMessage) {
                const message = this.pendingCapturePhoneMessage;
                this.pendingCapturePhoneMessage = null;
                this.pendingCapturePhoneResponse = null;

                this.showTypingIndicator();
                sendQuery(this.apiEndpoint, message, this.sessionId, this.config.responseSizeLimit)
                    .then(data => {
                        this.hideTypingIndicator();
                        if (data.session_id) {
                            this.sessionId = data.session_id;
                            saveSessionId(this.sessionId);
                        }
                        this.processMarkers(data, message);
                    })
                    .catch(error => {
                        this.hideTypingIndicator();
                        this.addMessage('system', 'Sorry, there was an error processing your request.');
                        console.error('Error sending query:', error);
                    });
            } else {
                this.pendingCapturePhoneResponse = null;
            }

        } catch (error) {
            console.error('Error submitting phone:', error);
            phoneError.textContent = 'Failed to submit phone. Please try again.';
            phoneError.style.display = 'block';
            submitBtn.disabled = false;
            submitBtn.textContent = 'Send';
        }
    }

    async declinePhone() {
        const form = this.shadowRoot.getElementById('phoneForm');
        if (form) form.remove();

        this.pendingCapturePhoneResponse = null;
        this.pendingCapturePhoneMessage = null;

        // Send decline signal to backend to allow flow transition
        this.showTypingIndicator();
        try {
            const data = await sendQuery(this.apiEndpoint, '_decline_', this.sessionId, this.config.responseSizeLimit);
            this.hideTypingIndicator();

            if (data.session_id) {
                this.sessionId = data.session_id;
                saveSessionId(this.sessionId);
            }

            // If backend handled the decline (has answer or markers), process it
            if (data.answer || data.redirect_url) {
                if (data.redirect_url) {
                    this.showRedirectConfirmation(data.redirect_url);
                } else {
                    this.processMarkers(data, '_decline_');
                }
            }
            // If backend didn't provide a response, no message is shown - user can continue chatting
        } catch (error) {
            this.hideTypingIndicator();
            console.error('Error sending decline signal:', error);
            // On error, silently continue - user can still chat
        }
    }

    showContactUsForm() {
        // Check if form already exists
        const existingForm = this.shadowRoot.getElementById('contactUsForm');
        if (existingForm) {
            return;
        }

        const formHTML = `
            <div class="message assistant-message" id="contactUsForm">
                <div class="contact-us-form">
                    <h3 style="margin-top: 0; margin-bottom: 15px; font-size: 16px; font-weight: 600;">${this.t('FORM_CONTACT_US_TITLE')}</h3>
                    <div class="form-group">
                        <label for="contactName" style="display: block; margin-bottom: 5px; font-weight: 500;">${this.t('FORM_NAME_LABEL')} <span style="color: red;">*</span></label>
                        <input type="text" id="contactName" name="name" placeholder="${this.t('FORM_YOUR_NAME').replace(' *', '')}" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; box-sizing: border-box;" required />
                        <span id="contactNameError" class="error-message" style="color: red; font-size: 12px; display: none;"></span>
                    </div>
                    <div class="form-group" style="margin-top: 10px;">
                        <label for="contactEmail" style="display: block; margin-bottom: 5px; font-weight: 500;">${this.t('FORM_EMAIL_LABEL')}</label>
                        <input type="email" id="contactEmail" name="email" placeholder="${this.t('FORM_EMAIL_PLACEHOLDER')}" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; box-sizing: border-box;" />
                        <span id="contactEmailError" class="error-message" style="color: red; font-size: 12px; display: none;"></span>
                    </div>
                    <div class="form-group" style="margin-top: 10px;">
                        <label for="contactPhone" style="display: block; margin-bottom: 5px; font-weight: 500;">${this.t('FORM_PHONE_LABEL')}</label>
                        <input type="tel" id="contactPhone" name="phone" placeholder="${this.t('FORM_PHONE_PLACEHOLDER')}" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; box-sizing: border-box;" />
                        <span id="contactPhoneError" class="error-message" style="color: red; font-size: 12px; display: none;"></span>
                    </div>
                    <div class="form-group" style="margin-top: 10px;">
                        <label for="contactMessage" style="display: block; margin-bottom: 5px; font-weight: 500;">${this.t('FORM_MESSAGE_LABEL')} <span style="color: red;">*</span></label>
                        <textarea id="contactMessage" name="message" rows="4" placeholder="${this.t('FORM_MESSAGE_PLACEHOLDER')}" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; box-sizing: border-box; resize: vertical;" required></textarea>
                        <span id="contactMessageError" class="error-message" style="color: red; font-size: 12px; display: none;"></span>
                    </div>
                    <p style="font-size: 12px; color: #666; margin-top: 10px;">${this.t('FORM_CONTACT_REQUIRED_NOTE')}</p>
                    <div class="form-actions" style="margin-top: 15px; display: flex; gap: 10px;">
                        <button id="submitContactUs" style="flex: 1; padding: 10px; background-color: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; font-weight: 500;">${this.t('FORM_SEND_MESSAGE')}</button>
                        <button id="cancelContactUs" style="padding: 10px 20px; background-color: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer;">${this.t('FORM_CANCEL')}</button>
                    </div>
                </div>
            </div>
        `;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = formHTML;
        this.messagesContainer.appendChild(tempDiv.firstElementChild);

        scrollToBottom(this.messagesContainer);

        const submitBtn = this.shadowRoot.getElementById('submitContactUs');
        const cancelBtn = this.shadowRoot.getElementById('cancelContactUs');
        const phoneInput = this.shadowRoot.getElementById('contactPhone');

        // Add phone auto-formatting
        if (phoneInput) {
            phoneInput.addEventListener('input', (e) => {
                // Format phone number as user types
                const cursorPosition = e.target.selectionStart;
                const oldValue = e.target.value;
                const oldLength = oldValue.length;

                const formattedValue = formatPhoneNumberRealTime(oldValue);
                e.target.value = formattedValue;

                // Adjust cursor position after formatting
                const newLength = formattedValue.length;
                const lengthDiff = newLength - oldLength;

                // If the formatted value is longer (e.g., added parenthesis or dash),
                // move the cursor forward
                if (lengthDiff > 0 && cursorPosition < newLength) {
                    e.target.setSelectionRange(cursorPosition + lengthDiff, cursorPosition + lengthDiff);
                } else {
                    e.target.setSelectionRange(cursorPosition, cursorPosition);
                }
            });
        }

        if (submitBtn && cancelBtn) {
            submitBtn.addEventListener('click', () => this.submitContactUs());
            cancelBtn.addEventListener('click', () => this.cancelContactUs());
        } else {
            console.error('Contact us form buttons not found:', { submitBtn, cancelBtn });
        }
    }

    async submitContactUs() {
        const nameInput = this.shadowRoot.getElementById('contactName');
        const emailInput = this.shadowRoot.getElementById('contactEmail');
        const phoneInput = this.shadowRoot.getElementById('contactPhone');
        const messageInput = this.shadowRoot.getElementById('contactMessage');

        const nameError = this.shadowRoot.getElementById('contactNameError');
        const emailError = this.shadowRoot.getElementById('contactEmailError');
        const phoneError = this.shadowRoot.getElementById('contactPhoneError');
        const messageError = this.shadowRoot.getElementById('contactMessageError');

        // Reset errors
        nameError.style.display = 'none';
        emailError.style.display = 'none';
        phoneError.style.display = 'none';
        messageError.style.display = 'none';

        let isValid = true;

        // Validate name
        if (!nameInput.value.trim()) {
            nameError.textContent = 'Name is required';
            nameError.style.display = 'block';
            isValid = false;
        }

        // Validate message
        if (!messageInput.value.trim()) {
            messageError.textContent = 'Message is required';
            messageError.style.display = 'block';
            isValid = false;
        }

        // Validate email OR phone (at least one required)
        const email = emailInput.value.trim();
        const phone = phoneInput.value.trim();

        if (!email && !phone) {
            phoneError.textContent = 'Either email or phone is required';
            phoneError.style.display = 'block';
            isValid = false;
        }

        // Validate email format if provided
        if (email && !validateEmail(email)) {
            emailError.textContent = 'Please enter a valid email address';
            emailError.style.display = 'block';
            isValid = false;
        }

        // Validate phone format if provided
        if (phone && !validatePhoneNumber(phone)) {
            phoneError.textContent = 'Please enter a valid phone number';
            phoneError.style.display = 'block';
            isValid = false;
        }

        if (!isValid) return;

        const submitBtn = this.shadowRoot.getElementById('submitContactUs');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending...';

        try {
            // Format phone number before submission
            const formattedPhone = phone ? formatPhoneNumber(phone) : '';

            // Submit to API
            await submitContactUsAPI(
                this.baseUrl,
                this.sessionId,
                {
                    name: nameInput.value.trim(),
                    email: email,
                    phone: formattedPhone,
                    message: messageInput.value.trim()
                }
            );

            // Remove form
            const form = this.shadowRoot.getElementById('contactUsForm');
            if (form) form.remove();

            // Show success message
            this.addMessage('assistant', 'Thank you for contacting us! We\'ll get back to you shortly.');

        } catch (error) {
            console.error('Error submitting contact form:', error);
            submitBtn.disabled = false;
            submitBtn.textContent = 'Send Message';

            // Show error message
            await handleAPIError(error, this.messagesContainer, this.language, 'GENERIC_ERROR');
        }
    }

    async cancelContactUs() {
        const form = this.shadowRoot.getElementById('contactUsForm');
        if (form) form.remove();

        // Send decline signal to backend to allow flow transition
        this.showTypingIndicator();
        try {
            const data = await sendQuery(this.apiEndpoint, '_decline_', this.sessionId, this.config.responseSizeLimit);
            this.hideTypingIndicator();

            if (data.session_id) {
                this.sessionId = data.session_id;
                saveSessionId(this.sessionId);
            }

            // If backend handled the decline (has answer or markers), process it
            if (data.answer || data.redirect_url) {
                if (data.redirect_url) {
                    this.showRedirectConfirmation(data.redirect_url);
                } else {
                    this.processMarkers(data, '_decline_');
                }
            }
            // No fallback message for contact us form - just silently close
        } catch (error) {
            this.hideTypingIndicator();
            console.error('Error sending decline signal:', error);
            // Silently fail - form is already removed
        }
    }

    async submitUserDetails() {
        const formData = validateUserDetailsForm(this.shadowRoot);
        if (!formData) return;

        const submitBtn = this.shadowRoot.getElementById('submitUserDetails');
        submitBtn.disabled = true;

        try {
            // Ensure we have a session ID
            if (!this.sessionId) {
                const initResponse = await sendQuery(this.apiEndpoint, "init", "");
                if (initResponse.session_id) {
                    this.sessionId = initResponse.session_id;
                    saveSessionId(this.sessionId);
                }
            }

            // Prepare chat history
            const chatHistory = this.messages.map(msg =>
                `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`
            ).join('\n');

            // Submit to API
            const captureResult = await submitUserDetailsAPI(
                this.baseUrl,
                this.sessionId,
                {
                    chatHistory,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phone: formData.phone,
                    email: formData.email,
                    consent: formData.consent
                },
                this.pendingRuleId
            );

            // Mark as captured
            this.userDetailsCaptured = true;
            saveUserDetailsCaptured(true);

            // Save the user details
            saveUserDetails(formData);
            this.userDetails = formData;

            // Replace the form with a non-editable summary instead of removing it
            const form = this.shadowRoot.getElementById('userDetailsForm');
            if (form) {
                const submittedHTML = getUserDetailsSubmittedHTML(formData, this.t.bind(this));
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = submittedHTML;
                form.replaceWith(tempDiv.firstElementChild);
            }

            // Show notification delivery feedback
            // When notification_sent is true, our capture handler sent the email (RAG path).
            // Clear all pending states — no resend needed, the notification is the final action.
            // When notification_sent is false, a conversation flow is handling the email,
            // so leave pending states intact for handlePostUserDetailsCapture to continue the flow.
            if (captureResult?.notification_sent === true) {
                this.addMessage('system', this.t('NOTIFICATION_SENT'));
                this.pendingCaptureDetailsResponse = null;
                this.pendingCaptureDetailsMessage = null;
            } else if (captureResult?.notification_sent === false) {
                this.addMessage('system', this.t('NOTIFICATION_FAILED'));
                this.pendingCaptureDetailsResponse = null;
                this.pendingCaptureDetailsMessage = null;
            }

            // Handle pending states - the flow's message will be shown in handlePostUserDetailsCapture()
            this.handlePostUserDetailsCapture();

        } catch (error) {
            console.error('Error submitting user details:', error);
            await this.handleAPIError(error, 'NETWORK_ERROR');
            submitBtn.disabled = false;
        }
    }

    handlePostUserDetailsCapture() {
        // Handle pending message resend
        if (this.pendingCaptureDetailsMessage) {
            const message = this.pendingCaptureDetailsMessage;
            this.pendingCaptureDetailsMessage = null;
            this.showTypingIndicator();
            sendQuery(this.apiEndpoint, message, this.sessionId, this.config.responseSizeLimit)
                .then(data => {
                    this.hideTypingIndicator();
                    if (data.session_id) {
                        this.sessionId = data.session_id;
                        saveSessionId(this.sessionId);
                    }
                    // Use processMarkers for full marker handling
                    this.processMarkers(data, message);
                })
                .catch(error => {
                    this.hideTypingIndicator();
                    this.addMessage('system', 'Sorry, there was an error processing your request.');
                    console.error('Error sending query:', error);
                });
            return;
        }

        if (this.pendingCaptureDetailsResponse !== null) {
            // Save the message before clearing it
            const successMessage = this.pendingCaptureDetailsResponse;
            this.pendingCaptureDetailsResponse = null;

            if (this.pendingCaptureAppointmentResponse) {
                // Show the flow's message
                if (successMessage) {
                    this.addMessage('assistant', successMessage);
                }
                this.showAppointmentForm();
                return;
            }

            if (this.pendingSendConversationResponse !== null) {
                // Show the flow's message before sending the email
                if (successMessage) {
                    this.addMessage('assistant', successMessage);
                }
                this.sendConversationEmail();
                return;
            }

            // Show the flow's message
            if (successMessage) {
                this.addMessage('assistant', successMessage);
            }
        }

        if (this.pendingTextResponse !== null) {
            // For response rules with text response, use the rule's message
            if (this.pendingTextResponse) {
                this.addMessage('assistant', this.pendingTextResponse);
            }
            this.pendingTextResponse = null;
            this.pendingRuleId = null;
            return;
        }

        if (this.pendingCaptureAppointmentResponse !== null) {
            if (this.pendingCaptureAppointmentResponse) {
                this.addMessage('assistant', this.pendingCaptureAppointmentResponse);
            }
            this.showAppointmentForm();
            return;
        }

        if (this.pendingSendConversationResponse !== null) {
            this.sendConversationEmail();
            return;
        }

        if (this.pendingFirstMessage) {
            const message = this.pendingFirstMessage;
            this.pendingFirstMessage = null;
            this.showTypingIndicator();
            
            sendQuery(this.apiEndpoint, message, this.sessionId, this.config.responseSizeLimit)
                .then(data => {
                    this.hideTypingIndicator();
                    if (data.session_id) {
                        this.sessionId = data.session_id;
                        saveSessionId(this.sessionId);
                    }
                    if (data.redirect_url) {
                        this.showRedirectConfirmation(data.redirect_url);
                    } else {
                        this.processMarkers(data, message);
                    }
                })
                .catch(error => {
                    this.hideTypingIndicator();
                    console.error('Error sending pending message:', error);
                });
        }
    }

    async declineUserDetails() {
        this.userDetailsCaptured = true;
        saveUserDetailsCaptured(true);

        const form = this.shadowRoot.getElementById('userDetailsForm');
        if (form) form.remove();

        this.pendingFirstMessage = null;
        this.pendingCaptureDetailsResponse = null;

        // Send decline signal to backend to allow flow transition
        this.showTypingIndicator();
        try {
            const data = await sendQuery(this.apiEndpoint, '_decline_', this.sessionId, this.config.responseSizeLimit);
            this.hideTypingIndicator();

            if (data.session_id) {
                this.sessionId = data.session_id;
                saveSessionId(this.sessionId);
            }

            // If backend handled the decline (has answer or markers), process it
            if (data.answer || data.redirect_url) {
                if (data.redirect_url) {
                    this.showRedirectConfirmation(data.redirect_url);
                } else {
                    this.processMarkers(data, '_decline_');
                }
            }
            // If backend didn't provide a response, no message is shown - user can continue chatting
        } catch (error) {
            this.hideTypingIndicator();
            console.error('Error sending decline signal:', error);
            // On error, silently continue - user can still chat
        }
    }

    showAppointmentForm() {
        const existingForm = this.shadowRoot.getElementById('appointmentForm');
        if (existingForm) return;

        const formHTML = getAppointmentFormHTML(this.userDetails, this.t.bind(this));
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = formHTML;
        const formElement = tempDiv.firstElementChild;
        this.messagesContainer.appendChild(formElement);

        setTimeout(() => {
            formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);

        setupAppointmentFormListeners(this.shadowRoot, this.userDetails);

        const submitBtn = this.shadowRoot.getElementById('submitAppointment');
        const declineBtn = this.shadowRoot.getElementById('declineAppointment');

        submitBtn.addEventListener('click', () => this.submitAppointment());
        declineBtn.addEventListener('click', () => this.declineAppointment());
    }

    async submitAppointment() {
        const appointmentData = validateAppointmentForm(this.shadowRoot, this.userDetails);
        if (!appointmentData) return;

        const submitBtn = this.shadowRoot.getElementById('submitAppointment');
        submitBtn.disabled = true;

        try {
            const data = await submitAppointmentAPI(
                this.baseUrl,
                this.sessionId,
                appointmentData,
                this.pendingAppointmentEmails
            );

            const form = this.shadowRoot.getElementById('appointmentForm');
            if (form) form.remove();

            this.pendingCaptureAppointmentResponse = null;

            if (!this.userDetails && appointmentData.userDetails) {
                saveUserDetails(appointmentData.userDetails);
                this.userDetails = appointmentData.userDetails;
            }

            // Send a query to continue the flow (similar to phone form handling)
            this.showTypingIndicator();
            const continueData = await sendQuery(this.apiEndpoint, 'continue', this.sessionId, this.config.responseSizeLimit);
            this.hideTypingIndicator();

            if (continueData.session_id) {
                this.sessionId = continueData.session_id;
                saveSessionId(this.sessionId);
            }

            if (continueData.redirect_url) {
                this.showRedirectConfirmation(continueData.redirect_url);
            } else {
                this.processMarkers(continueData, 'continue');
            }

        } catch (error) {
            console.error('Error submitting appointment:', error);
            await this.handleAPIError(error, 'NETWORK_ERROR');
            submitBtn.disabled = false;
        }
    }

    async declineAppointment() {
        const form = this.shadowRoot.getElementById('appointmentForm');
        if (form) form.remove();

        this.pendingCaptureAppointmentResponse = null;

        // Send decline signal to backend to allow flow transition
        this.showTypingIndicator();
        try {
            const data = await sendQuery(this.apiEndpoint, '_decline_', this.sessionId, this.config.responseSizeLimit);
            this.hideTypingIndicator();

            if (data.session_id) {
                this.sessionId = data.session_id;
                saveSessionId(this.sessionId);
            }

            // If backend handled the decline (has answer or markers), process it
            if (data.answer || data.redirect_url) {
                if (data.redirect_url) {
                    this.showRedirectConfirmation(data.redirect_url);
                } else {
                    this.processMarkers(data, '_decline_');
                }
            } else {
                // Fallback to default decline message
                this.addMessage('assistant', 'No problem! Let me know if you need anything else.');
            }
        } catch (error) {
            this.hideTypingIndicator();
            console.error('Error sending decline signal:', error);
            // Fallback to showing decline message
            this.addMessage('assistant', 'No problem! Let me know if you need anything else.');
        }
    }

    async sendConversationEmail() {
        try {
            const data = await sendConversationEmailAPI(
                this.baseUrl,
                this.sessionId,
                this.userDetails,
                this.pendingConversationEmails
            );

            const successMessage = data.message || this.t('CONVERSATION_SENT');
            if (successMessage) {
                this.addMessage('assistant', successMessage);
            }
            this.pendingSendConversationResponse = null;

        } catch (error) {
            console.error('Error sending conversation email:', error);
            await this.handleAPIError(error, 'NETWORK_ERROR');
        }
    }

    showRedirectConfirmation(redirectUrl) {
        showRedirectConfirmation(
            this.messagesContainer,
            redirectUrl,
            null, // onYes handled in the function
            () => this.addMessage('assistant', "No problem! Feel free to ask me anything else.")
        );
    }

    showSuggestedQuestionsOrPhoneCapture() {
        // Set welcome mode when showing suggested questions
        if (!this.config.previewMode) {
            this.inWelcomeMode = true;
            hideBackButton(this.backBtn, this.header);
        }

        showSuggestedQuestions(
            this.messagesContainer,
            this.config.suggestedQuestions,
            (question, flowId) => {
                this.handleSuggestedQuestionClick(question, flowId);
            }
        );
    }

    /**
     * Reset the widget state and start previewing a conversation flow.
     * Used by admin panel to test flows from the visual editor.
     * @param {string} triggerMessage - The message to send to trigger the flow
     */
    async resetAndPreviewFlow(triggerMessage) {
        // Clear existing messages from UI
        this.messagesContainer.innerHTML = '';

        // Reset internal state
        this.messages = [];
        this.sessionId = null;
        this.isTyping = false;
        this.userDetailsCaptured = false;
        this.userDetails = null;
        this.pendingFirstMessage = null;
        this.pendingCaptureDetailsResponse = null;
        this.pendingCaptureDetailsMessage = null;
        this.pendingCaptureAppointmentResponse = null;
        this.pendingSendConversationResponse = null;
        this.pendingTextResponse = null;
        this.pendingRuleId = null;
        this.currentOptions = null;
        this.hasShownSuggestedQuestions = false;
        this.pendingAppointmentEmails = null;
        this.pendingConversationEmails = null;
        this.hasRetriedAfterError = false;
        this.inWelcomeMode = false;
        this._chatSaved = false;

        // Clear ALL session storage for this widget to ensure complete reset
        // Preview mode uses 'chat_widget' prefix (no property ID suffix)
        sessionStorage.removeItem('chat_widget_session_id');
        sessionStorage.removeItem('chat_widget_messages');
        sessionStorage.removeItem('chat_widget_user_details');
        sessionStorage.removeItem('chat_widget_user_details_captured');
        sessionStorage.removeItem('chat_widget_shown_suggestions');

        // Initialize a new session
        await this.initializeSession();

        // Open the widget
        this.open();

        // Send the trigger message to start the flow
        if (triggerMessage) {
            await this.sendMessage(triggerMessage);
        }
    }

}

// Export the ChatWidget class
export default ChatWidget;

// Auto-initialize if data attribute is present
document.addEventListener('DOMContentLoaded', () => {
    // Prevent duplicate initialization if script is loaded multiple times
    if (window.chatWidget) {
        console.warn('Chat widget already initialized, skipping duplicate initialization');
        return;
    }

    const widgetScript = document.querySelector('script[data-chat-widget-isolated]');
    if (widgetScript) {
        let openOnLoad = true;
        const openOnLoadAttr = widgetScript.getAttribute('data-open-on-load');
        if (openOnLoadAttr !== null) {
            openOnLoad = openOnLoadAttr !== 'false';
        }

        let openOnLoadDelay = 0;
        const openOnLoadDelayAttr = widgetScript.getAttribute('data-open-on-load-delay');
        if (openOnLoadDelayAttr) {
            const parsed = parseInt(openOnLoadDelayAttr);
            if (!isNaN(parsed) && parsed >= 0) {
                openOnLoadDelay = parsed;
            }
        }

        let responseSizeLimit = 0;
        const responseSizeLimitAttr = widgetScript.getAttribute('data-response-size-limit');
        if (responseSizeLimitAttr) {
            const parsed = parseInt(responseSizeLimitAttr);
            if (!isNaN(parsed)) {
                responseSizeLimit = parsed;
            }
        }

        // Parse suggested questions from JSON attribute
        let suggestedQuestions = [];
        const suggestedQuestionsAttr = widgetScript.getAttribute('data-suggested-questions');
        if (suggestedQuestionsAttr) {
            try {
                suggestedQuestions = JSON.parse(suggestedQuestionsAttr);
            } catch (e) {
                console.error('Invalid JSON for data-suggested-questions:', e);
            }
        }

        // Check if auto-config is enabled
        const autoConfig = widgetScript.hasAttribute('data-auto-config');

        if (autoConfig) {
            // Determine base URL from script src or data-api-endpoint
            let baseUrl = '';

            const apiEndpointAttr = widgetScript.getAttribute('data-api-endpoint');
            if (apiEndpointAttr && apiEndpointAttr.startsWith('http')) {
                baseUrl = apiEndpointAttr.split('/api/')[0];
            } else {
                const scriptSrc = widgetScript.getAttribute('src');
                if (scriptSrc && scriptSrc.startsWith('http')) {
                    const url = new URL(scriptSrc);
                    baseUrl = url.origin;
                }
            }

            const cacheBuster = new Date().getTime();

            // Fetch both widget config and suggested questions in parallel
            Promise.all([
                fetch(baseUrl + '/api/widget-config/public?_=' + cacheBuster).then(r => r.json()),
                fetch(baseUrl + '/api/suggested-questions?_=' + cacheBuster).then(r => r.json())
            ])
                .then(([serverConfig, questionsData]) => {
                    const suggestedQuestions = (questionsData.questions || []).map(rule => ({
                        id: rule.id,
                        question: rule.suggested_question,
                        response: rule.response_type === 'text' ? rule.text_response : null,
                        redirectUrl: rule.response_type === 'redirect' ? rule.redirect_url : null,
                        requireUserDetails: rule.require_user_details || false,
                        captureIntroMessage: rule.capture_intro_message || ''
                    }));

                    let fullApiEndpoint = serverConfig.api_endpoint;
                    if (fullApiEndpoint && !fullApiEndpoint.startsWith('http')) {
                        fullApiEndpoint = baseUrl + fullApiEndpoint;
                    }

                    const config = {
                        apiEndpoint: fullApiEndpoint,
                        botName: serverConfig.bot_name,
                        botStatus: serverConfig.bot_status,
                        welcomeMessage: serverConfig.welcome_message,
                        placeholder: serverConfig.placeholder,
                        openOnLoad: serverConfig.open_on_load,
                        openOnLoadDelay: serverConfig.open_on_load_delay || 0,
                        responseSizeLimit: serverConfig.response_size_limit,
                        color: serverConfig.color,
                        mobileNotificationTitle: serverConfig.mobile_notification_title,
                        mobileNotificationText: serverConfig.mobile_notification_text,
                        botIcon: serverConfig.bot_icon,
                        suggestedQuestions: suggestedQuestions,
                        maxHeightDesktop: serverConfig.max_height_desktop
                    };
                    window.chatWidget = new ChatWidget(config);
                })
                .catch(error => {
                    console.error('Failed to load widget configuration:', error);
                    window.chatWidget = new ChatWidget({});
                });
        } else {
            // Use manual configuration from data attributes
            const config = {
                apiEndpoint: widgetScript.getAttribute('data-api-endpoint') || '/api/query',
                botName: widgetScript.getAttribute('data-bot-name') || 'AI Assistant',
                botStatus: widgetScript.getAttribute('data-bot-status') || 'Online',
                welcomeMessage: widgetScript.getAttribute('data-welcome-message') || 'Hi! I\'m your AI assistant. How can I help you today?',
                placeholder: widgetScript.getAttribute('data-placeholder') || 'Type your message...',
                openOnLoad: openOnLoad,
                openOnLoadDelay: openOnLoadDelay,
                responseSizeLimit: responseSizeLimit,
                color: widgetScript.getAttribute('data-color') || '#667eea',
                mobileNotificationTitle: widgetScript.getAttribute('data-mobile-notification-title') || 'Need help?',
                mobileNotificationText: widgetScript.getAttribute('data-mobile-notification-text') || 'Chat with our AI assistant',
                botIcon: widgetScript.getAttribute('data-bot-icon') || '🤖',
                suggestedQuestions: suggestedQuestions
            };

            window.chatWidget = new ChatWidget(config);
        }
    }
});

// Export ChatWidget class globally for admin panel preview mode
window.ChatWidget = ChatWidget;
